import { listingImageSrc } from './listingCropsRun';
import type { HeroBox } from './realImages';

// ONE LISTING CROP, CUT OUT FOR REAL — the rectangle as its own small picture, not a
// photograph with a box drawn on it.
//
// Everything on screen shows a crop by moving the whole photograph inside a frame that clips
// it (see boxZoomStyle), which is right for a browser and useless for a model: what would go
// into the call is the chair, plus a sentence about where in it to look. Cut down to the box,
// a fourteen-millimetre button fills the frame and its colour and finish are the picture.
//
// CUT HERE AND NOT ON THE SERVER because there is no image library there — the backend fetches
// bytes and hands them on, and cropping would mean adding Pillow to it. The browser already
// has a canvas, already has the picture on screen, and the same-origin proxy exists precisely
// so a canvas may read these pixels: a cross-origin image taints the canvas and toDataURL
// throws on it.
const MAX_SIDE = 384;

// Room around the rectangle. A crop filled edge to edge with its subject shows the subject
// and none of what settles the question — the same argument the thumbnails make, and it
// matters more here: the model is being asked what a part is made of, and the panel it is
// screwed into is what tells wood from wood-effect.
const MARGIN = 0.04;

// WHAT A CUT TAKES OF THE PHOTOGRAPH: the box and its margin, clamped to the picture, 0..1. One
// definition, used by the cut and by anything mapping a point in the cut back onto the photograph
// (see the materials press, whose patches are drawn on these close-ups).
export const cutRect = ([x0, y0, x1, y1]: HeroBox): HeroBox => [
  Math.max(0, x0 - MARGIN), Math.max(0, y0 - MARGIN), Math.min(1, x1 + MARGIN), Math.min(1, y1 + MARGIN),
];

// A LISTING PHOTOGRAPH BY ITS URL — on a retailer's server, so it goes through the proxy that
// makes a canvas able to read it at all.
export function cutCrop(url: string, bbox: HeroBox): Promise<string | null> {
  return cutCropFrom(listingImageSrc(url), bbox);
}

// THE SAME CUT FROM A PICTURE THE APP ALREADY SERVES — a stored photograph addressed by its id,
// which needs no proxy and must not be given one: wrapping an app URL in the proxy asks the
// server to fetch itself by a path it will not recognise.
export function cutCropFrom(src: string, bbox: HeroBox): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const [cx0, cy0, cx1, cy1] = cutRect(bbox);
      const sx = cx0 * img.width;
      const sy = cy0 * img.height;
      const sw = cx1 * img.width - sx;
      const sh = cy1 * img.height - sy;
      if (sw < 2 || sh < 2) {
        resolve(null);
        return;
      }
      const scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sw * scale));
      canvas.height = Math.max(1, Math.round(sh * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      try {
        // JPEG, because these are photographs: a PNG of one is several times the bytes for
        // no detail a model reads, and every one of them travels in a POST body.
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}
